import { useApiClient } from '@m43/core';
import {
    ApiResultDeleteBoloAdditionalInfoResponse,
    ApiResultGetBoloAdditionalInfosResponse,
    ApiResultSaveBoloAdditionalInfoResponse,
    SaveBoloAdditionalInfoRequest,
    BoloResource,
} from '@mark43/cad-api';
import { AxiosResponse } from 'axios-cad-nx';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useAdditionalInfosBoloResource() {
    const apiClient = useApiClient();

    /**
     * Deletes an Additional Info.
     *
     * @param boloInfoId - ID of the Additional Infos to delete.
     * @returns No value returned.
     * @throws The `AXIOSResponse` of a failed request to delete the Additional Info.
     */
    const deleteInfo = async (
        boloInfoId: number
    ): Promise<AxiosResponse<ApiResultDeleteBoloAdditionalInfoResponse>> => {
        return await apiClient.request<BoloResource.DeleteBoloAdditionalInfos>({
            method: 'POST',
            url: `bolo/additional_info/delete`,
            data: {
                boloLinkId: boloInfoId,
            },
            shouldFailSilently: true,
        });
    };

    /**
     * Retrieves a list of Additional Infos for a specified BOLO.
     *
     * @param boloId - ID of the BOLO to retrieve the Additional Infos for.
     * @param cursor - If cursor is present, the list will start one ID past the cursor (direction is dependent on `direction`).
     *                 If cursor is not present the list will start at the newest or oldest Additional Info, depending on `direction`).
     * @param limit - The maximum number of Additional Infos to return.
     * @returns The `AXIOSResponse` of a successful request to get the Additional Infos list.
     * @throws The `AXIOSResponse` of a failed request to get the Additional Infos list.
     */
    const getInfosByCursor = async ({
        boloId,
        limit: pageSize = 100,
        cursor,
    }: {
        limit?: number;
        cursor?: number;
        boloId: number;
    }): Promise<AxiosResponse<ApiResultGetBoloAdditionalInfosResponse>> => {
        return await apiClient.request<BoloResource.GetBoloAdditionalInfos>({
            method: 'POST',
            url: `bolo/additional_info`,
            data: { boloId, lastId: cursor, size: pageSize },
            shouldFailSilently: true,
        });
    };

    /**
     * Create a new Additional Info/Narrative
     *
     * @param data - The new additional info to be created.
     * @returns The `AXIOSResponse` of a successful request to create the additional info.
     * @throws The `AXIOSResponse` of a failed request to create the additional info.
     */
    const createAdditionalInfo = async (
        data: SaveBoloAdditionalInfoRequest
    ): Promise<AxiosResponse<ApiResultSaveBoloAdditionalInfoResponse>> => {
        return await apiClient.request<BoloResource.SaveBoloAdditionalInfo>({
            method: 'PUT',
            url: `bolo/additional_info`,
            data,
            shouldFailSilently: true,
        });
    };

    return {
        createAdditionalInfo,
        deleteInfo,
        getInfosByCursor,
    };
}
